import { AuthTracking } from "@/lib/tracking/auth";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  AlertDescription,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputPassword,
  Typography,
  LoadingButton,
  Stack,
} from "@altimateai/lego";
import { isFreeStack } from "@/helpers/tenant";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { decodeToken } from "react-jwt";
import { BaseContainer } from "./components/base";
import { registrationSchema, validateFormPassword } from "./helper";
import { useMutation } from "@tanstack/react-query";
import { postRegisterInvite } from "@/helpers/apis";
import { loginUser } from "@/services/auth";
import { cn } from "@/lib/utils";
import { PasswordValidation } from "./PasswordValidator";
import { useAppDispatch } from "@/modules/app/useAppContext";
import { setLoggedinUser } from "@/modules/app/appSlice";

const PASSWORD_INPUT_ID = "register-user-password-input";

type RegistrationFormValues = z.infer<typeof registrationSchema>;

interface DecodedToken {
  email: string;
  company: string;
  exp: number;
  nbf?: number;
}

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFreeUser = isFreeStack();
  const { token } = useParams<{ token: string }>();

  const { mutate, error, isPending } = useMutation({
    mutationFn: postRegisterInvite,
    onSuccess: response => {
      if (response?.auth_token) {
        AuthTracking.registerSucceeded(isFreeUser ? "free" : "invite");
        loginUser(response);
        dispatch(setLoggedinUser(response));
        if (isFreeUser) {
          navigate("/settings/api-key");
        } else {
          navigate("/quickstart");
        }
      }
    },
    onError: (err) => {
      AuthTracking.registerFailed(
        isFreeUser ? "free" : "invite",
        err instanceof Error ? err.message : String(err)
      );
    },
  });

  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      name: "",
      password: "",
    },
    mode: "onBlur",
  });

  const [validatePassword, setValidatePassword] = useState([0, 0, 0, 0, 0]);
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);

  const decodedToken = decodeToken<DecodedToken>(token ?? "");
  if (decodedToken && decodedToken.exp < Date.now() / 1000) {
    const state = { inviteDate: decodedToken.nbf };
    return <Navigate to="/expired" state={state} />;
  }

  if (!decodedToken) {
    return <Navigate to="/expired" />;
  }

  const { email, company: company_name } = decodedToken;

  const setPasswordElement = (e: React.FocusEvent<HTMLInputElement>) => {
    setActiveElement(e.target);
  };

  const clearPasswordElement = () => {
    setActiveElement(null);
  };

  const isFormDisabled = () => validatePassword.includes(-1) || validatePassword.includes(0);

  const onSubmit = (values: RegistrationFormValues) => {
    AuthTracking.registerSubmitted(isFreeUser ? "free" : "invite");
    mutate({ ...values, token: token ?? "" });
  };

  // Convert API error to string for display
  const getApiErrorMessage = (): string | undefined => {
    if (!error) return undefined;
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    if (typeof error === "object" && "detail" in error) {
      return (error as { detail: string }).detail;
    }
    return "Registration failed. Please try again.";
  };

  const apiError = getApiErrorMessage();
  const showPasswordValidation =
    activeElement?.id === PASSWORD_INPUT_ID || validatePassword.includes(-1);

  return (
    <BaseContainer
      footer={
        <div>
          <Typography variant="body" className="al-text-gray-500 al-font-normal">
            Already have an account?{" "}
          </Typography>
          <Link to="/login" className="al-text-primary">
            Sign In
          </Link>
        </div>
      }
    >
      <Typography variant="page-heading">
        Sign Up
      </Typography>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="al-mt-3">
          <Stack direction="column" gap="md">
            <FormItem>
              <FormLabel>Organization</FormLabel>
              <FormControl>
                <Input name="company_name" type="text" value={company_name} disabled />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input name="email" type="email" value={email} disabled />
              </FormControl>
            </FormItem>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} type="text" placeholder="Enter Name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor={PASSWORD_INPUT_ID}>Password</FormLabel>
                  <div
                    className={cn(
                      showPasswordValidation && "al-rounded-b-none",
                      validatePassword.includes(-1) && "al-border-action-red"
                    )}
                  >
                    <FormControl>
                      <div className="al-relative">
                        <InputPassword
                          id={PASSWORD_INPUT_ID}
                          placeholder="Enter password"
                          onBlur={() => {
                            setValidatePassword(prev =>
                              prev.map(element => (element === 0 ? -1 : element))
                            );
                            clearPasswordElement();
                            field.onBlur();
                          }}
                          onChange={e => {
                            field.onChange(e);
                            validateFormPassword(e.target.value, setValidatePassword);
                          }}
                          value={field.value}
                          disabled={form.formState.isSubmitting}
                          onFocus={setPasswordElement}
                        />
                      </div>
                    </FormControl>
                  </div>
                  {showPasswordValidation && (
                    <PasswordValidation validatePassword={validatePassword} />
                  )}
                </FormItem>
              )}
            />

            <div>
              <Typography variant="body" size="xs" className="al-text-gray-500">
                <span>By creating an account, you agree to the </span>
                <Link
                  to="https://www.altimate.ai/terms"
                  target="_blank"
                  className="al-text-dark al-font-medium"
                >
                  Terms & Conditions
                </Link>
                <span> and our </span>
                <Link
                  to="https://www.altimate.ai/privacy"
                  target="_blank"
                  className="al-text-dark al-font-medium"
                >
                  Privacy Policy
                </Link>
              </Typography>
            </div>

            {apiError && (
              <Alert variant="destructive">
                <AlertDescription>{apiError}</AlertDescription>
              </Alert>
            )}

            <LoadingButton
              type="submit"
              className="al-w-full al-mt-1"
              disabled={isFormDisabled()}
              loading={isPending}
            >
              Sign Up
            </LoadingButton>
          </Stack>
        </form>
      </Form>
    </BaseContainer>
  );
};

export { Register };
